import { create } from 'zustand';
import {
  hasDataLockRejection,
  isDataLockError,
} from 'pages/ReportsPage/store/reportsDataHealth.store';
import { EMPLOYEE_ENGAGEMENT_DATA_LOCKED_STUB } from 'pages/ReportsPage/components/AnalyticsDashboards/EmployeeEngagementDashboard/store/employeeEngagement.stub';
import {
  EmployeeEngagementDashboardStore,
  HoursWorkedEntry,
  PickupEntry,
  LastAppOpenedEntry,
  ShiftAcceptanceEntry,
  TeamMemberCountResponse,
  HoursWorkedByTeamMemberResponse,
  PickupsByTeamMemberResponse,
  ShiftAcceptanceLeaderboardResponse,
  LastAppOpenedResponse,
  CancellationByPositionEntry,
  CancellationRateByPositionResponse,
  TopFulfillmentEntry,
  TopIndividualFulfillmentResponse,
  TopCancellationEntry,
  TopIndividualCancellationsResponse,
  CalloutAcceptanceEntry,
  CalloutAcceptanceRateResponse,
} from 'pages/ReportsPage/components/AnalyticsDashboards/EmployeeEngagementDashboard/store/employeeEngagement.store.types';
import {
  LAST_APP_OPENED_BUCKETS,
  LAST_APP_OPENED_BUCKET_KEYS,
  formatEmploymentStatus,
} from 'pages/ReportsPage/components/AnalyticsDashboards/EmployeeEngagementDashboard/EmployeeEngagementDashboard.constants';
import { devtoolsInNonProd } from 'store/zustand/index.utils';
import { createDashboardsRequest } from 'pages/ReportsPage/components/AnalyticsDashboards/createDashboardsRequest';
import { unwrapDashboardRoot } from 'pages/ReportsPage/components/AnalyticsDashboards/unwrapDashboardRoot';
import { fetchDashboardPage } from 'pages/ReportsPage/components/AnalyticsDashboards/fetchDashboardPage';
import {
  DASHBOARDS_TEAM_MEMBERS_ALL,
  DASHBOARDS_TEAM_MEMBERS_FULL_TIME,
  DASHBOARDS_TEAM_MEMBERS_PART_TIME,
  DASHBOARDS_TEAM_MEMBERS_FLEX,
  DASHBOARDS_TEAM_MEMBERS_CASUAL,
  DASHBOARDS_TEAM_MEMBERS_MOBILE_USERS,
  DASHBOARDS_TEAM_MEMBERS_LAST_APP_OPENED,
  DASHBOARDS_TEAM_MEMBERS_HOURS_WORKED_BY_TEAM_MEMBER,
  DASHBOARDS_TEAM_MEMBERS_PICKUPS_BY_TEAM_MEMBER,
  DASHBOARDS_TEAM_MEMBERS_SHIFT_ACCEPTANCE_LEADERBOARD,
  DASHBOARDS_SHIFTS_CANCELLATION_RATE_BY_POSITION,
  DASHBOARDS_TEAM_MEMBERS_TOP_INDIVIDUAL_FULFILLMENT,
  DASHBOARDS_TEAM_MEMBERS_TOP_INDIVIDUAL_CANCELLATIONS,
  DASHBOARDS_TEAM_MEMBERS_CALLOUT_ACCEPTANCE_RATE,
} from 'constants/endpoints';
import { isSelectionCleared } from 'pages/ReportsPage/store/analyticsSelection.utils';

const INITIAL_STATE = {
  totalTeamMembers: 0,
  fullTimeMembers: 0,
  partTimeMembers: 0,
  smartphoneAppUsers: 0,
  casualMembers: 0,
  flexMembers: 0,
  hoursWorked: null,
  hoursWorkedPage: 1,
  hoursWorkedTotalPages: 1,
  hoursWorkedLoadingMore: false,
  pickups: null,
  lastAppOpened: null,
  shiftAcceptanceLeaderboard: null,
  cancellationByPosition: null,
  cancellationByPositionPage: 1,
  cancellationByPositionTotalPages: 1,
  cancellationByPositionLoadingMore: false,
  cancellationByPositionError: false,
  topFulfillment: null,
  topFulfillmentPage: 1,
  topFulfillmentTotalPages: 1,
  topFulfillmentLoading: false,
  topFulfillmentError: false,
  topCancellations: null,
  topCancellationsPage: 1,
  topCancellationsTotalPages: 1,
  topCancellationsLoading: false,
  topCancellationsError: false,
  calloutAcceptance: null,
  calloutAcceptancePage: 1,
  calloutAcceptanceTotalPages: 1,
  calloutAcceptanceLoading: false,
  calloutAcceptanceError: false,
  isLoading: false,
  error: null,
};

function toNum(value: number | string | null | undefined): number {
  const parsed = typeof value === 'string' ? parseFloat(value) : value;
  return typeof parsed === 'number' && !Number.isNaN(parsed) ? parsed : 0;
}

function buildFilterQuery(
  dateRange: [string, string],
  locationIds: string[] | null,
  positions: string[] | null,
  employmentTypes: string[],
): Record<string, unknown> {
  const filters: Record<string, unknown> = {
    time_range: {
      start_time: dateRange[0],
      end_time: dateRange[1],
    },
  };

  if (locationIds && locationIds.length > 0) {
    filters.community_ids = locationIds.map(Number);
  }
  if (positions && positions.length > 0) {
    filters.position_ids = positions.map(Number);
  }
  if (employmentTypes.length > 0) {
    filters.employment_statuses = employmentTypes;
  }

  return { filters };
}

// The count endpoints (all/full_time/part_time/flex/casual) read current team-member records —
// no time range; they accept only community_ids and position_ids.
function buildHeadcountQuery(
  locationIds: string[] | null,
  positions: string[] | null,
): Record<string, unknown> {
  const filters: Record<string, unknown> = {};

  if (locationIds && locationIds.length > 0) {
    filters.community_ids = locationIds.map(Number);
  }
  if (positions && positions.length > 0) {
    filters.position_ids = positions.map(Number);
  }

  return { filters };
}

function buildLastAppOpenedQuery(
  locationIds: string[] | null,
  positions: string[] | null,
  employmentTypes: string[],
): Record<string, unknown> {
  const filters: Record<string, unknown> = {};

  if (locationIds && locationIds.length > 0) {
    filters.community_ids = locationIds.map(Number);
  }
  if (positions && positions.length > 0) {
    filters.position_ids = positions.map(Number);
  }
  if (employmentTypes.length > 0) {
    filters.employment_statuses = employmentTypes;
  }

  return { filters };
}

function mapCount(response: unknown, rootKey: string): number {
  // API-5599: each headcount endpoint now nests `{ total }` under its own root key.
  return toNum(unwrapDashboardRoot<TeamMemberCountResponse>(response, rootKey)?.total);
}

function mapHoursWorked(
  rows: HoursWorkedByTeamMemberResponse['hours_worked_by_team_member'],
): HoursWorkedEntry[] {
  return (rows ?? [])
    .map((item) => ({
      teamMember: item.team_member_name,
      hoursWorked: toNum(item.hours_worked),
    }))
    .sort((a, b) => b.hoursWorked - a.hoursWorked);
}

function mapPickups(response: PickupsByTeamMemberResponse): PickupEntry[] {
  return (response?.pickups_by_team_member ?? [])
    .map((item) => ({
      teamMemberName: item.team_member_name,
      type: formatEmploymentStatus(item.employment_status),
      numberOfShifts: toNum(item.shift_count),
    }))
    .sort((a, b) => b.numberOfShifts - a.numberOfShifts);
}

function mapShiftAcceptanceLeaderboard(
  response: ShiftAcceptanceLeaderboardResponse,
): ShiftAcceptanceEntry[] {
  return (response?.shift_acceptance_leaderboard ?? [])
    .map((item) => ({
      rank: 0,
      teamMember: item.team_member_name,
      position: item.position_name,
      type: formatEmploymentStatus(item.employment_status),
      shiftsAccepted: toNum(item.shift_count),
    }))
    .sort((a, b) => b.shiftsAccepted - a.shiftsAccepted)
    .map((entry, index) => ({ ...entry, rank: index + 1 }));
}

function mapLastAppOpened(response: LastAppOpenedResponse): LastAppOpenedEntry[] {
  const buckets = response?.last_app_opened;
  return LAST_APP_OPENED_BUCKETS.map((bucket, index) => ({
    bucket,
    count: toNum(buckets?.[LAST_APP_OPENED_BUCKET_KEYS[index]]),
  }));
}

function mapCancellationByPosition(
  rows: CancellationRateByPositionResponse['cancellation_rate_by_position'],
): CancellationByPositionEntry[] {
  return rows.map((item) => ({
    positionId: String(item.position_id),
    position: item.position_name,
    totalShiftCount: toNum(item.total_shift_count),
    cancelledShiftCount: toNum(item.cancelled_shift_count),
    cancelledPrct: toNum(item.cancelled_prct),
  }));
}

function mapTopFulfillment(response: TopIndividualFulfillmentResponse): TopFulfillmentEntry[] {
  return (response?.top_individual_fulfillment ?? []).map((item) => ({
    teamMemberId: item.team_member_id,
    teamMemberName: item.team_member_name,
    position: item.position_name,
    shiftCount: toNum(item.shift_count),
    averageResponseTimeHours: toNum(item.average_response_time_hours),
  }));
}

function mapTopCancellations(response: TopIndividualCancellationsResponse): TopCancellationEntry[] {
  return (response?.top_individual_cancellations ?? []).map((item) => ({
    teamMemberId: item.team_member_id,
    teamMemberName: item.team_member_name,
    cancelledCounts: toNum(item.cancelled_counts),
    acceptCounts: toNum(item.accept_counts),
    cancellationRate: item.cancellation_rate == null ? null : toNum(item.cancellation_rate),
  }));
}

function mapCalloutAcceptance(response: CalloutAcceptanceRateResponse): CalloutAcceptanceEntry[] {
  return (response?.callout_acceptance_rate ?? []).map((item) => ({
    teamMemberId: item.team_member_id,
    teamMemberName: item.team_member_name,
    position: item.position_name,
    type: formatEmploymentStatus(item.employment_status_label),
    calloutsSent: toNum(item.callouts_sent),
    calloutsAccepted: toNum(item.callouts_accepted),
    acceptanceRate: item.acceptance_rate == null ? null : toNum(item.acceptance_rate),
    averageResponseSpeedMinutes:
      item.average_response_speed_minutes == null
        ? null
        : toNum(item.average_response_speed_minutes),
  }));
}

let fetchId = 0;
let topFulfillmentFetchId = 0;
let topCancellationsFetchId = 0;
let calloutAcceptanceFetchId = 0;
let hoursWorkedBaseQuery: Record<string, unknown> | null = null;
let cancellationByPositionBaseQuery: Record<string, unknown> | null = null;

export const employeeEngagementDashboardStore = create<EmployeeEngagementDashboardStore>()(
  devtoolsInNonProd((set, get) => ({
    ...INITIAL_STATE,

    fetchData: (dateRange, locationIds, positions, employmentTypes) => {
      if (isSelectionCleared(locationIds, positions)) {
        fetchId++;
        set({ ...INITIAL_STATE });
        return () => {
          fetchId++;
        };
      }
      const currentFetchId = ++fetchId;
      set({ isLoading: true, error: null });

      const query = buildFilterQuery(dateRange, locationIds, positions, employmentTypes);
      const headcountQuery = buildHeadcountQuery(locationIds, positions);
      const lastAppOpenedQuery = buildLastAppOpenedQuery(locationIds, positions, employmentTypes);
      hoursWorkedBaseQuery = query;
      cancellationByPositionBaseQuery = query;

      const request = <T>(url: string, q: Record<string, unknown>) =>
        createDashboardsRequest<T>({ url, method: 'GET', query: q });

      const allRequest = request<TeamMemberCountResponse>(
        DASHBOARDS_TEAM_MEMBERS_ALL(),
        headcountQuery,
      );
      const fullTimeRequest = request<TeamMemberCountResponse>(
        DASHBOARDS_TEAM_MEMBERS_FULL_TIME(),
        headcountQuery,
      );
      const partTimeRequest = request<TeamMemberCountResponse>(
        DASHBOARDS_TEAM_MEMBERS_PART_TIME(),
        headcountQuery,
      );
      const flexRequest = request<TeamMemberCountResponse>(
        DASHBOARDS_TEAM_MEMBERS_FLEX(),
        headcountQuery,
      );
      const casualRequest = request<TeamMemberCountResponse>(
        DASHBOARDS_TEAM_MEMBERS_CASUAL(),
        headcountQuery,
      );
      const mobileUsersRequest = request<TeamMemberCountResponse>(
        DASHBOARDS_TEAM_MEMBERS_MOBILE_USERS(),
        headcountQuery,
      );
      const lastAppOpenedRequest = request<LastAppOpenedResponse>(
        DASHBOARDS_TEAM_MEMBERS_LAST_APP_OPENED(),
        lastAppOpenedQuery,
      );
      const hoursWorkedRequest = fetchDashboardPage<
        HoursWorkedByTeamMemberResponse,
        HoursWorkedByTeamMemberResponse['hours_worked_by_team_member'][number]
      >({
        url: DASHBOARDS_TEAM_MEMBERS_HOURS_WORKED_BY_TEAM_MEMBER(),
        query,
        page: 1,
        selectItems: (body) => body.hours_worked_by_team_member ?? [],
      });
      const pickupsRequest = request<PickupsByTeamMemberResponse>(
        DASHBOARDS_TEAM_MEMBERS_PICKUPS_BY_TEAM_MEMBER(),
        query,
      );
      const shiftAcceptanceRequest = request<ShiftAcceptanceLeaderboardResponse>(
        DASHBOARDS_TEAM_MEMBERS_SHIFT_ACCEPTANCE_LEADERBOARD(),
        query,
      );
      const cancellationByPositionRequest = fetchDashboardPage<
        CancellationRateByPositionResponse,
        CancellationRateByPositionResponse['cancellation_rate_by_position'][number]
      >({
        url: DASHBOARDS_SHIFTS_CANCELLATION_RATE_BY_POSITION(),
        query,
        page: 1,
        selectItems: (body) => body.cancellation_rate_by_position ?? [],
      });

      get().fetchTopFulfillment(1, dateRange, locationIds, positions, employmentTypes);
      get().fetchTopCancellations(1, dateRange, locationIds, positions, employmentTypes);
      get().fetchCalloutAcceptance(1, dateRange, locationIds, positions, employmentTypes);

      Promise.allSettled([
        allRequest,
        fullTimeRequest,
        partTimeRequest,
        flexRequest,
        casualRequest,
        mobileUsersRequest,
        lastAppOpenedRequest,
        hoursWorkedRequest,
        pickupsRequest,
        shiftAcceptanceRequest,
        cancellationByPositionRequest,
      ]).then((results) => {
        const isDataLocked = hasDataLockRejection(results);
        if (currentFetchId !== fetchId) return;
        if (isDataLocked) {
          set({ ...INITIAL_STATE, ...EMPLOYEE_ENGAGEMENT_DATA_LOCKED_STUB, isLoading: false });
          return;
        }

        const [
          all,
          fullTime,
          partTime,
          flex,
          casual,
          mobileUsers,
          lastAppOpened,
          hoursWorked,
          pickups,
          shiftAcceptance,
          cancellationByPosition,
        ] = results;

        const hoursWorkedData = hoursWorked.status === 'fulfilled' ? hoursWorked.value : null;
        const cancellationByPositionData =
          cancellationByPosition.status === 'fulfilled' ? cancellationByPosition.value : null;

        set({
          totalTeamMembers:
            all.status === 'fulfilled' ? mapCount(all.value.data, 'all_team_members') : 0,
          fullTimeMembers:
            fullTime.status === 'fulfilled' ? mapCount(fullTime.value.data, 'full_time') : 0,
          partTimeMembers:
            partTime.status === 'fulfilled' ? mapCount(partTime.value.data, 'part_time') : 0,
          flexMembers: flex.status === 'fulfilled' ? mapCount(flex.value.data, 'flex') : 0,
          casualMembers: casual.status === 'fulfilled' ? mapCount(casual.value.data, 'casual') : 0,
          smartphoneAppUsers:
            mobileUsers.status === 'fulfilled'
              ? mapCount(mobileUsers.value.data, 'mobile_users')
              : 0,
          lastAppOpened:
            lastAppOpened.status === 'fulfilled'
              ? mapLastAppOpened(lastAppOpened.value.data)
              : null,
          hoursWorked: hoursWorkedData ? mapHoursWorked(hoursWorkedData.items) : null,
          hoursWorkedPage: hoursWorkedData?.meta?.current_page ?? 1,
          hoursWorkedTotalPages: hoursWorkedData?.meta?.total_pages ?? 1,
          hoursWorkedLoadingMore: false,
          pickups: pickups.status === 'fulfilled' ? mapPickups(pickups.value.data) : null,
          shiftAcceptanceLeaderboard:
            shiftAcceptance.status === 'fulfilled'
              ? mapShiftAcceptanceLeaderboard(shiftAcceptance.value.data)
              : null,
          cancellationByPosition: cancellationByPositionData
            ? mapCancellationByPosition(cancellationByPositionData.items)
            : null,
          cancellationByPositionPage: cancellationByPositionData?.meta?.current_page ?? 1,
          cancellationByPositionTotalPages: cancellationByPositionData?.meta?.total_pages ?? 1,
          cancellationByPositionLoadingMore: false,
          cancellationByPositionError: cancellationByPosition.status === 'rejected',
          isLoading: false,
          error: null,
        });
      });

      return () => {
        fetchId++;
      };
    },

    fetchTopFulfillment: (page, dateRange, locationIds, positions, employmentTypes) => {
      const currentFetchId = ++topFulfillmentFetchId;
      set({ topFulfillmentLoading: true, topFulfillmentError: false });

      const query = {
        ...buildFilterQuery(dateRange, locationIds, positions, employmentTypes),
        page,
      };

      createDashboardsRequest<TopIndividualFulfillmentResponse>({
        url: DASHBOARDS_TEAM_MEMBERS_TOP_INDIVIDUAL_FULFILLMENT(),
        method: 'GET',
        query,
      }).then(
        (response) => {
          if (currentFetchId !== topFulfillmentFetchId) return;
          const { meta } = response.data;
          set({
            topFulfillment: mapTopFulfillment(response.data),
            topFulfillmentPage: meta?.current_page ?? page,
            topFulfillmentTotalPages: meta?.total_pages ?? 1,
            topFulfillmentLoading: false,
            topFulfillmentError: false,
          });
        },
        (error) => {
          if (currentFetchId !== topFulfillmentFetchId) return;
          if (isDataLockError(error)) {
            set({
              topFulfillment: EMPLOYEE_ENGAGEMENT_DATA_LOCKED_STUB.topFulfillment ?? null,
              topFulfillmentPage: 1,
              topFulfillmentTotalPages: 1,
              topFulfillmentLoading: false,
              topFulfillmentError: false,
            });
            return;
          }
          set({ topFulfillment: null, topFulfillmentLoading: false, topFulfillmentError: true });
        },
      );
    },

    fetchTopCancellations: (page, dateRange, locationIds, positions, employmentTypes) => {
      const currentFetchId = ++topCancellationsFetchId;
      set({ topCancellationsLoading: true, topCancellationsError: false });

      const query = {
        ...buildFilterQuery(dateRange, locationIds, positions, employmentTypes),
        page,
      };

      createDashboardsRequest<TopIndividualCancellationsResponse>({
        url: DASHBOARDS_TEAM_MEMBERS_TOP_INDIVIDUAL_CANCELLATIONS(),
        method: 'GET',
        query,
      }).then(
        (response) => {
          if (currentFetchId !== topCancellationsFetchId) return;
          const { meta } = response.data;
          set({
            topCancellations: mapTopCancellations(response.data),
            topCancellationsPage: meta?.current_page ?? page,
            topCancellationsTotalPages: meta?.total_pages ?? 1,
            topCancellationsLoading: false,
            topCancellationsError: false,
          });
        },
        (error) => {
          if (currentFetchId !== topCancellationsFetchId) return;
          if (isDataLockError(error)) {
            set({
              topCancellations: EMPLOYEE_ENGAGEMENT_DATA_LOCKED_STUB.topCancellations ?? null,
              topCancellationsPage: 1,
              topCancellationsTotalPages: 1,
              topCancellationsLoading: false,
              topCancellationsError: false,
            });
            return;
          }
          set({
            topCancellations: null,
            topCancellationsLoading: false,
            topCancellationsError: true,
          });
        },
      );
    },

    fetchCalloutAcceptance: (page, dateRange, locationIds, positions, employmentTypes) => {
      const currentFetchId = ++calloutAcceptanceFetchId;
      set({ calloutAcceptanceLoading: true, calloutAcceptanceError: false });

      const query = {
        ...buildFilterQuery(dateRange, locationIds, positions, employmentTypes),
        page,
      };

      createDashboardsRequest<CalloutAcceptanceRateResponse>({
        url: DASHBOARDS_TEAM_MEMBERS_CALLOUT_ACCEPTANCE_RATE(),
        method: 'GET',
        query,
      }).then(
        (response) => {
          if (currentFetchId !== calloutAcceptanceFetchId) return;
          const { meta } = response.data;
          set({
            calloutAcceptance: mapCalloutAcceptance(response.data),
            calloutAcceptancePage: meta?.current_page ?? page,
            calloutAcceptanceTotalPages: meta?.total_pages ?? 1,
            calloutAcceptanceLoading: false,
            calloutAcceptanceError: false,
          });
        },
        (error) => {
          if (currentFetchId !== calloutAcceptanceFetchId) return;
          if (isDataLockError(error)) {
            set({
              calloutAcceptance: EMPLOYEE_ENGAGEMENT_DATA_LOCKED_STUB.calloutAcceptance ?? null,
              calloutAcceptancePage: 1,
              calloutAcceptanceTotalPages: 1,
              calloutAcceptanceLoading: false,
              calloutAcceptanceError: false,
            });
            return;
          }
          set({
            calloutAcceptance: null,
            calloutAcceptanceLoading: false,
            calloutAcceptanceError: true,
          });
        },
      );
    },

    loadMoreHoursWorked: () => {
      const { hoursWorkedLoadingMore, hoursWorkedPage, hoursWorkedTotalPages } = get();
      if (
        hoursWorkedLoadingMore ||
        hoursWorkedPage >= hoursWorkedTotalPages ||
        !hoursWorkedBaseQuery
      ) {
        return;
      }
      const currentFetchId = fetchId;
      const nextPage = hoursWorkedPage + 1;
      set({ hoursWorkedLoadingMore: true });

      fetchDashboardPage<
        HoursWorkedByTeamMemberResponse,
        HoursWorkedByTeamMemberResponse['hours_worked_by_team_member'][number]
      >({
        url: DASHBOARDS_TEAM_MEMBERS_HOURS_WORKED_BY_TEAM_MEMBER(),
        query: hoursWorkedBaseQuery,
        page: nextPage,
        selectItems: (body) => body.hours_worked_by_team_member ?? [],
      })
        .then(({ items, meta }) => {
          if (currentFetchId !== fetchId) return;
          set((state) => ({
            hoursWorked: [...(state.hoursWorked ?? []), ...mapHoursWorked(items)],
            hoursWorkedPage: meta?.current_page ?? nextPage,
            hoursWorkedTotalPages: meta?.total_pages ?? state.hoursWorkedTotalPages,
            hoursWorkedLoadingMore: false,
          }));
        })
        .catch(() => {
          if (currentFetchId !== fetchId) return;
          set({ hoursWorkedLoadingMore: false });
        });
    },
    loadMoreCancellationByPosition: () => {
      const {
        cancellationByPositionLoadingMore,
        cancellationByPositionPage,
        cancellationByPositionTotalPages,
      } = get();
      if (
        cancellationByPositionLoadingMore ||
        cancellationByPositionPage >= cancellationByPositionTotalPages ||
        !cancellationByPositionBaseQuery
      ) {
        return;
      }
      const currentFetchId = fetchId;
      const nextPage = cancellationByPositionPage + 1;
      set({ cancellationByPositionLoadingMore: true });

      fetchDashboardPage<
        CancellationRateByPositionResponse,
        CancellationRateByPositionResponse['cancellation_rate_by_position'][number]
      >({
        url: DASHBOARDS_SHIFTS_CANCELLATION_RATE_BY_POSITION(),
        query: cancellationByPositionBaseQuery,
        page: nextPage,
        selectItems: (body) => body.cancellation_rate_by_position ?? [],
      })
        .then(({ items, meta }) => {
          if (currentFetchId !== fetchId) return;
          set((state) => ({
            cancellationByPosition: [
              ...(state.cancellationByPosition ?? []),
              ...mapCancellationByPosition(items),
            ],
            cancellationByPositionPage: meta?.current_page ?? nextPage,
            cancellationByPositionTotalPages:
              meta?.total_pages ?? state.cancellationByPositionTotalPages,
            cancellationByPositionLoadingMore: false,
          }));
        })
        .catch(() => {
          if (currentFetchId !== fetchId) return;
          set({ cancellationByPositionLoadingMore: false });
        });
    },
    setLoading: (isLoading) => set({ isLoading }),
    setError: (error) => set({ error }),
    reset: () => {
      fetchId = 0;
      hoursWorkedBaseQuery = null;
      cancellationByPositionBaseQuery = null;
      set(INITIAL_STATE);
    },
  })),
);

export const useEmployeeEngagementDashboardStore = employeeEngagementDashboardStore;
